import { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useLogger } from '~Hooks';
import { BOOKINGS_POLL_INTERVAL_MS, useBookings } from '~Providers/BookingsProvider';
import { GetBookings_operativeViewer_bookingsForVenueAndDate } from '~Providers/BookingsProvider/api/__queries__';

type Booking = GetBookings_operativeViewer_bookingsForVenueAndDate;

/**
 * Bookings for a date picked in the calendar, polled so auto-accepted bookings show up
 * without re-selecting the date. `undefined` means today, which the BookingsProvider covers.
 */
export const useBookingsForDate = (date: Date | undefined) => {
  const logger = useLogger('bookings-for-date');
  const { fetchBookingsForDate } = useBookings();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);

  const dateString = date ? DateTime.fromJSDate(date).toFormat('yyyy-MM-dd') : null;

  // Lets in-flight requests check they still belong to the date on screen
  const activeDateRef = useRef(dateString);
  activeDateRef.current = dateString;

  // Initial load for the date, showing the loader.
  // Only depends on the (stable) fetch function, not the whole context value: the provider's
  // value isn't memoised, so depending on it re-fetched on every provider render and flashed
  // the loader. Refreshes after actions go through `refresh()` instead.
  useEffect(() => {
    if (!dateString) {
      setBookings([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchBookingsForDate(dateString)
      .then((fetched) => {
        if (!cancelled) setBookings(fetched ?? []);
      })
      .catch(() => {
        if (!cancelled) setBookings([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateString, fetchBookingsForDate]);

  // Silent refetch: no loader, and keeps the current list if the request fails
  const refresh = useCallback(async () => {
    const requestedDate = activeDateRef.current;
    if (!requestedDate) return;

    try {
      const fetched = await fetchBookingsForDate(requestedDate);
      if (activeDateRef.current === requestedDate) {
        setBookings(fetched ?? []);
      }
    } catch (error) {
      logger.warn('Failed to refresh bookings for selected date', {
        date: requestedDate,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
    }
  }, [fetchBookingsForDate, logger]);

  useEffect(() => {
    if (!dateString) return undefined;

    const interval = setInterval(refresh, BOOKINGS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dateString, refresh]);

  return { bookings, loading, refresh };
};
